import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import CLOSE_WHITE_ICON from '@public/assets/common/ai_close_white.svg'
import INFORMATION_WHITE_ICON from '@public/assets/information_white.svg'
import { cn } from '@utils/mergeClassNames'
import { useConnectedAppsStore } from '@/stores/useConnectedAppsStore'
import type { AliveAppsNotification } from '../types'
import { getAliveAppsNotifications } from '../utils/aliveAppsNotification'
import AuditLoggingNotificationBar from './AuditLoggingNotificationBar'

const getNotificationKey = (notification: AliveAppsNotification) =>
  `${notification.app}-${notification.status}-${notification.settingsTab ?? ''}`

function AliveAppsNotificationBar() {
  const [dismissedNotificationKeys, setDismissedNotificationKeys] = useState<
    Set<string>
  >(new Set())

  const navigate = useNavigate()

  const {
    agentEnabled,
    desktopAgentStatus,
    chromeExtensionStatus,
    edgeExtensionStatus,
  } = useConnectedAppsStore()

  const notifications = getAliveAppsNotifications({
    agentEnabled,
    desktopAgentStatus,
    chromeExtensionStatus,
    edgeExtensionStatus,
  })

  const visibleNotifications = notifications.filter(
    notification =>
      !dismissedNotificationKeys.has(getNotificationKey(notification))
  )

  const handleGetHelpClick = (notification: AliveAppsNotification) => {
    if (notification.app === 'webExtension' && notification.settingsTab) {
      navigate(`/settings?tab=${notification.settingsTab}`)
      return
    }

    if (notification.app === 'desktopAgent') {
      navigate('/settings?tab=desktop-agent')
    }
  }

  const handleNotificationDismiss = (notification: AliveAppsNotification) => {
    setDismissedNotificationKeys(previousKeys =>
      new Set(previousKeys).add(getNotificationKey(notification))
    )
  }

  return (
    <>
      {visibleNotifications.map((notification, index) => (
        <div
          key={getNotificationKey(notification)}
          role="alert"
          className={cn(
            'flex max-h-[54px] w-full items-start justify-between gap-1 px-5 py-3 text-[14px] font-normal leading-5 text-white shadow-sm sm:items-center',
            notification.level === 'error' ? 'bg-[#DD352C]' : 'bg-[#FF8A03]',
            index > 0 && 'border-0 border-t border-solid border-white/70'
          )}
        >
          <div className="flex min-w-0 flex-1 items-start gap-2.5 sm:items-center">
            <img
              className="mt-0.5 h-5 w-5 shrink-0 sm:mt-0"
              src={INFORMATION_WHITE_ICON}
              alt=""
            />
            <span className="min-w-0 flex-1 break-words">
              {notification.message}
              <button
                type="button"
                className="ml-2 inline-flex h-[30px] min-w-[99px] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-full border-0 bg-white px-3 py-[5px] text-center align-middle text-sm font-normal leading-5 text-[#333333] sm:hidden"
                onClick={() => handleGetHelpClick(notification)}
              >
                <span>Get Help</span>
              </button>
            </span>
          </div>

          <button
            type="button"
            className="hidden h-[30px] min-w-[99px] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-full border-0 bg-white px-3 py-[5px] text-center text-sm font-normal leading-5 text-[#333333] sm:inline-flex"
            onClick={() => handleGetHelpClick(notification)}
          >
            <span>Get Help</span>
          </button>

          <button
            type="button"
            className="ml-2 inline-flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0"
            aria-label={`Close ${notification.level} notification`}
            onClick={() => handleNotificationDismiss(notification)}
          >
            <img className="h-4 w-4" src={CLOSE_WHITE_ICON} alt="" />
          </button>
        </div>
      ))}
      <AuditLoggingNotificationBar
        key="audit-logging-notification"
        showDivider={visibleNotifications.length > 0}
      />
    </>
  )
}

export default AliveAppsNotificationBar
